/**
 * LeadContactSummary — first thing the sales team sees when opening a lead.
 *
 * Surfaces the most actionable contact data (email, phone, instagram,
 * country, language, villa interest, source) at the top of both
 * LeadSidePanel and LeadDetailPage. Supports an inline view↔edit toggle
 * for the contact fields backed by PATCH /leads/{id}; channels and
 * preferences keep living in their own specialised cards.
 */
import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  EnvelopeSimple, Phone, Translate, Tag, Globe, MapPin, InstagramLogo,
  NotePencil, Check, Signpost, X,
} from "@phosphor-icons/react";
import { landingLabel, isInvestorReferral } from "@/lib/leadLanding";

const LANGUAGE_LABEL = {
  es: "Español",
  en: "Inglés",
};

const SOURCE_LABEL = {
  web:        "Web",
  whatsapp:   "WhatsApp",
  gmail:      "Gmail",
  sheets:     "Sheets",
  referral:   "Referido",
  landing:    "Landing",
  manual:     "Manual",
  meta_ads:   "Meta Ads",
};

export function sourceLabel(value) {
  if (!value) return null;
  return SOURCE_LABEL[String(value).toLowerCase()] || String(value);
}

// Accepts handle, @handle, instagram.com/handle, https://instagram.com/handle.
// Returns { handle, url } or null when nothing extractable.
function parseInstagram(raw) {
  if (!raw) return null;
  let v = String(raw).trim();
  if (!v) return null;
  v = v.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "");
  v = v.replace(/^@+/, "");
  v = v.split(/[/?#]/)[0];
  if (!v) return null;
  return { handle: `@${v}`, url: `https://instagram.com/${v}` };
}

function isValidEmail(s) {
  if (!s) return true; // empty allowed (will be sent as null)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

// Free-text spellings that make `lead.interest` redundant with the
// structured villa_model_preference enum (see dedupeInterest below).
const VILLA_MODEL_TEXT = {
  dolce_vistas: ["dolce vistas", "dolce vista"],
  dolce_prive:  ["dolce prive"],
  either:       ["ambas", "ambos", "cualquiera", "either"],
};

function normText(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function Row({ icon: Icon, label, value, missing, testid }) {
  const isMissing = value == null || value === "";
  return (
    <div className="flex items-start gap-2.5 text-sm" data-testid={testid || `lead-contact-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="shrink-0 mt-0.5 w-7 h-7 rounded-md bg-stone-100 text-stone-500 border border-stone-200 flex items-center justify-center">
        <Icon size={13} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-stone-400">{label}</div>
        {isMissing ? (
          <div className="text-stone-400 italic text-xs">{missing}</div>
        ) : (
          <div className="text-stone-800 truncate">{value}</div>
        )}
      </div>
    </div>
  );
}

/**
 * `embedded`   — drop the dv-card chrome (the parent supplies the card),
 *                for the merged "Información del comprador" surface.
 * `twoColumn`  — compact 2-col grid (sm+) for view AND edit rows; also
 *                folds the Landing badge into the grid as a regular row.
 * `dedupeInterest` — hide the free-text "Villa / modelo de interés" row
 *                when it adds nothing over the structured
 *                villa_model_preference shown in the buyer profile.
 * Defaults keep the historical layout for LeadSidePanel and others.
 */
export default function LeadContactSummary({
  lead, onChanged, testid = "lead-contact-summary", editTrigger = 0,
  embedded = false, twoColumn = false, dedupeInterest = false,
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);

  // External callers can request edit mode by incrementing `editTrigger`
  // (e.g. the LeadHero "Editar contacto" quick action). Initial 0 is ignored.
  useEffect(() => {
    if (editTrigger > 0) setEditing(true);
  }, [editTrigger]);

  // Reset draft when entering edit mode or when the underlying lead changes.
  useEffect(() => {
    if (!editing) return;
    setDraft({
      name: lead?.name || "",
      email: lead?.email || "",
      phone: lead?.phone || "",
      country: lead?.country || "",
      language: lead?.language || "",
      instagram: lead?.instagram || "",
    });
    setError("");
  }, [editing, lead?.lead_id, lead?.name, lead?.email, lead?.phone, lead?.country, lead?.language, lead?.instagram]);

  if (!lead) return null;

  const email = (lead.email || "").trim();
  const phone = (lead.phone || "").trim();
  const country = (lead.country || "").trim();
  const language = lead.language ? (LANGUAGE_LABEL[lead.language] || lead.language) : null;
  const interest = (lead.interest || "").trim();
  const source = sourceLabel(lead.source);
  const landing = lead.landing_source;
  const landingFriendly = landing ? landingLabel(landing) : null;
  const referralAccent = isInvestorReferral(landing);
  const ig = parseInstagram(lead.instagram);

  const save = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) { setError("El nombre no puede estar vacío."); return; }
    const emailTrim = draft.email.trim();
    if (!isValidEmail(emailTrim)) { setError("Email no válido."); return; }
    setBusy(true); setError("");
    try {
      await api.patch(`/leads/${lead.lead_id}`, {
        name,
        email: emailTrim || null,
        phone: draft.phone.trim() || null,
        country: draft.country.trim() || null,
        language: draft.language || null,
        instagram: draft.instagram.trim() || null,
      });
      setEditing(false);
      onChanged && onChanged();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  // §Legacy interest dedup (display only, both backend fields intact):
  // the free-text row disappears when the structured preference already
  // covers it — either because the text is just a spelling of the enum
  // value, or because the text is empty and the enum is set.
  const structuredPref = lead.villa_model_preference || null;
  const interestRedundant = dedupeInterest && !!structuredPref && (
    !interest ||
    (VILLA_MODEL_TEXT[structuredPref] || []).includes(normText(interest))
  );

  const emailRow = (
    <Row
      icon={EnvelopeSimple}
      label="Email"
      value={email ? <a href={`mailto:${email}`} className="text-stone-800 hover:text-stone-900 hover:underline truncate inline-block max-w-full">{email}</a> : ""}
      missing="Sin email"
    />
  );
  const phoneRow = (
    <Row
      icon={Phone}
      label="Teléfono / WhatsApp"
      value={phone ? <a href={`tel:${phone}`} className="text-stone-800 hover:text-stone-900 hover:underline">{phone}</a> : ""}
      missing="Sin teléfono"
    />
  );
  const instagramRow = (
    <Row
      icon={InstagramLogo}
      label="Instagram"
      value={ig ? (
        <a
          href={ig.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-stone-800 hover:text-stone-900 hover:underline"
          data-testid="lead-contact-instagram-link"
        >
          {ig.handle}
        </a>
      ) : ""}
      missing="Sin Instagram"
      testid="lead-contact-row-instagram"
    />
  );
  const countryRow  = <Row icon={MapPin}    label="País"   value={country}  missing="País no indicado" />;
  const languageRow = <Row icon={Translate} label="Idioma" value={language} missing="Idioma no indicado" />;
  const sourceRow   = <Row icon={Globe}     label="Fuente" value={source}   missing="Fuente no indicada" />;
  const interestRow = interestRedundant ? null : (
    <Row icon={Tag} label="Villa / modelo de interés" value={interest} missing="Interés no indicado" />
  );
  const landingBadge = landingFriendly ? (
    <span
      className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${
        referralAccent
          ? "bg-amber-50 text-amber-900 border-amber-200"
          : "bg-stone-50 text-stone-700 border-stone-200"
      }`}
      data-testid="lead-contact-landing"
    >
      {landingFriendly}
    </span>
  ) : null;

  return (
    <div className={embedded ? "space-y-3" : "dv-card space-y-3"} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-medium">
          Contacto
        </div>
        {onChanged && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid="lead-contact-edit-btn"
          >
            <NotePencil size={12} /> Editar
          </button>
        )}
      </div>

      {!editing && (twoColumn ? (
        // Compact workspace layout — pairs read left→right:
        // Email | Teléfono · Instagram | País · Idioma | Fuente ·
        // Villa interés | Landing. One column on mobile.
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
          {emailRow}
          {phoneRow}
          {instagramRow}
          {countryRow}
          {languageRow}
          {sourceRow}
          {interestRow}
          <Row
            icon={Signpost}
            label="Landing"
            value={landingBadge}
            missing="Origen no registrado"
            testid="lead-contact-row-landing"
          />
        </div>
      ) : (
        <div className="space-y-2.5">
          {emailRow}
          {phoneRow}
          {instagramRow}
          {countryRow}
          {languageRow}
          {interestRow}
          {sourceRow}
        </div>
      ))}

      {editing && draft && (
        <div className="space-y-2.5" data-testid="lead-contact-edit-form">
          <div className={twoColumn ? "grid sm:grid-cols-2 gap-3" : "space-y-2.5"}>
          <Field label="Nombre">
            <input
              type="text"
              className="dv-input"
              value={draft.name}
              onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
              data-testid="lead-contact-edit-name"
              required
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              className="dv-input"
              value={draft.email}
              onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))}
              data-testid="lead-contact-edit-email"
            />
          </Field>
          <Field label="Teléfono / WhatsApp">
            <input
              type="tel"
              className="dv-input"
              value={draft.phone}
              onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
              data-testid="lead-contact-edit-phone"
            />
          </Field>
          <Field label="Instagram">
            <input
              type="text"
              className="dv-input"
              placeholder="@usuario o URL"
              value={draft.instagram}
              onChange={(e) => setDraft(d => ({ ...d, instagram: e.target.value }))}
              data-testid="lead-contact-edit-instagram"
            />
          </Field>
          <Field label="País">
            <input
              type="text"
              className="dv-input"
              value={draft.country}
              onChange={(e) => setDraft(d => ({ ...d, country: e.target.value }))}
              data-testid="lead-contact-edit-country"
            />
          </Field>
          <Field label="Idioma">
            <select
              className="dv-input"
              value={draft.language}
              onChange={(e) => setDraft(d => ({ ...d, language: e.target.value }))}
              data-testid="lead-contact-edit-language"
            >
              <option value="">Sin indicar</option>
              <option value="es">Español</option>
              <option value="en">Inglés</option>
            </select>
          </Field>
          </div>
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setEditing(false); setError(""); }}
              disabled={busy}
              className="text-xs px-3 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
              data-testid="lead-contact-edit-cancel"
            >
              <X size={12} /> Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="text-xs px-3 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="lead-contact-edit-save"
            >
              <Check size={12} weight="bold" /> {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {/* Source / landing badge — legacy footer (1-col mode only; the
          compact grid folds Landing in as a regular row). */}
      {!editing && !twoColumn && (
        <div className="pt-2 border-t border-stone-100">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Landing</div>
          {landingBadge || (
            <span className="text-stone-400 italic text-xs">Origen no registrado</span>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
